import { useEffect, useState, type DragEvent } from 'react';
import { TopBar } from '../components/TopBar';
import { SystemTile } from '../components/SystemTile';
import { api, type Me, type System } from '../api';

interface Props {
  me: Me;
  onSignOut: () => void;
}

export function PortalPage({ me, onSignOut }: Props) {
  const [systems, setSystems] = useState<System[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [overId, setOverId] = useState<number | null>(null);

  useEffect(() => {
    api
      .listSystems()
      .then(setSystems)
      .catch((err) => setError((err as Error).message));
  }, []);

  function onDragStart(e: DragEvent<HTMLDivElement>, id: number) {
    setDraggingId(id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(id));
  }

  function onDragOver(e: DragEvent<HTMLDivElement>, id: number) {
    if (draggingId === null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (overId !== id) setOverId(id);
  }

  function onDragLeave(id: number) {
    setOverId((curr) => (curr === id ? null : curr));
  }

  async function onDrop(e: DragEvent<HTMLDivElement>, targetId: number) {
    e.preventDefault();
    const sourceId = draggingId;
    setDraggingId(null);
    setOverId(null);
    if (sourceId === null || sourceId === targetId || !systems) return;

    const from = systems.findIndex((s) => s.id === sourceId);
    const to = systems.findIndex((s) => s.id === targetId);
    if (from < 0 || to < 0) return;

    const next = systems.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setSystems(next);

    try {
      await api.reorderMySystems(next.map((s) => s.id));
    } catch (err) {
      setError((err as Error).message);
    }
  }

  function onDragEnd() {
    setDraggingId(null);
    setOverId(null);
  }

  const today = new Date();
  const dateLine = today.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

  const issueNumber = String(((today.getFullYear() - 2024) * 52 + weekOfYear(today)) % 100).padStart(2, '0');

  return (
    <div className="relative min-h-screen paper-grain">
      <TopBar me={me} onSignOut={onSignOut} />

      <main className="relative mx-auto max-w-7xl px-6 md:px-10">
        {/* ─────────── EDITORIAL HERO ─────────── */}
        <section className="relative pb-16 pt-14 md:pb-24 md:pt-20">
          {/* Decorative wave / arc — the only "ocean" nod. Single bold curve. */}
          <DecorativeArc />

          <div className="relative grid grid-cols-12 gap-6">
            {/* Mast: issue meta */}
            <div className="col-span-12 mb-6 flex flex-wrap items-center justify-between gap-4 border-y hair-strong py-3 font-mono text-[10px] uppercase tracking-widestest text-cream-500 md:mb-10">
              <span className="flex items-center gap-3">
                <span className="text-coral">●</span>
                Issue №{issueNumber}
                <span className="text-cream-300">/</span>
                {dateLine}
              </span>
              <span className="flex items-center gap-3">
                {systems?.length ?? '—'} Systems Indexed
                <span className="text-cream-300">/</span>
                Surfboard Internal
              </span>
            </div>

            {/* Display hero — extreme scale jump, asymmetric */}
            <div className="col-span-12 md:col-span-9 animate-rise">
              <p className="ix mb-5">№ Welcome</p>
              <h1 className="font-display text-[14vw] font-light leading-[0.88] tracking-tightest text-cream md:text-[8.5rem]">
                Hello,{' '}
                <span
                  className="italic text-coral"
                  style={{ fontVariationSettings: "'opsz' 144, 'SOFT' 100, 'WONK' 1" }}
                >
                  {firstName(me)}.
                </span>
              </h1>
            </div>

            {/* Sidebar block: pull-quote / instructions */}
            <aside
              className="col-span-12 mt-6 flex flex-col justify-end border-l-2 border-coral pl-5 md:col-span-3 md:mt-0 animate-rise"
              style={{ animationDelay: '120ms' }}
            >
              <p className="font-display text-[20px] leading-snug text-cream-700 text-pretty">
                Every system you can touch, gathered in one volume.
              </p>
              <p className="mt-4 font-mono text-[11px] uppercase tracking-widestest text-cream-500">
                {me.isAdmin ? 'Editor · You can curate this issue.' : 'Reader edition.'}
              </p>
            </aside>
          </div>
        </section>

        {/* ─────────── SECTION HEADER ─────────── */}
        <div className="flex flex-wrap items-baseline justify-between gap-4 border-b hair-strong pb-4">
          <div className="flex items-baseline gap-4">
            <span className="font-mono text-[11px] uppercase tracking-widestest text-coral">§ 01</span>
            <h2 className="font-display text-3xl font-medium tracking-tightish text-cream">Systems</h2>
            <span className="hidden font-mono text-[11px] uppercase tracking-widestest text-cream-500 md:inline">
              Drag tiles to rearrange — your layout is saved.
            </span>
          </div>
          <span className="font-mono text-[11px] uppercase tracking-widestest text-cream-500">
            {systems?.length ?? 0} entries
          </span>
        </div>

        {/* ─────────── ERRORS ─────────── */}
        {error && (
          <div className="mt-6 border-l-2 border-coral bg-coral/10 px-4 py-3 font-mono text-[12px] text-coral-300">
            {error}
          </div>
        )}

        {/* ─────────── GRID ─────────── */}
        <section className="pb-24 pt-8">
          {systems === null ? (
            <TilesSkeleton />
          ) : systems.length === 0 ? (
            <EmptyState isAdmin={me.isAdmin} />
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {systems.map((system, i) => {
                const isDragging = draggingId === system.id;
                const isOver = overId === system.id && draggingId !== null && draggingId !== system.id;
                return (
                  <div
                    key={system.id}
                    draggable
                    onDragStart={(e) => onDragStart(e, system.id)}
                    onDragOver={(e) => onDragOver(e, system.id)}
                    onDragLeave={() => onDragLeave(system.id)}
                    onDrop={(e) => onDrop(e, system.id)}
                    onDragEnd={onDragEnd}
                    style={{ animationDelay: `${Math.min(i, 9) * 60}ms` }}
                    className={`animate-rise cursor-grab active:cursor-grabbing ${
                      isDragging ? 'opacity-30' : ''
                    } ${isOver ? 'outline outline-2 outline-coral outline-offset-2' : ''}`}
                  >
                    <SystemTile system={system} index={i} total={systems.length} />
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* ─────────── SIGNATURE FOOTER ─────────── */}
        <footer className="flex flex-col gap-6 border-t hair-strong py-10 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="font-display text-5xl font-light tracking-tightest text-cream">
              Surfboard<span className="text-coral">.</span>
            </p>
            <p className="mt-2 font-mono text-[10px] uppercase tracking-widestest text-cream-500">
              portal.surfboard.cloud · internal · {today.getFullYear()}
            </p>
          </div>
          <div className="flex items-baseline gap-6 font-mono text-[10px] uppercase tracking-widestest text-cream-500">
            <span>
              <span className="text-cream">{String(systems?.length ?? 0).padStart(2, '0')}</span>{' '}
              systems
            </span>
            <span>·</span>
            <span>Set in Fraunces, Inter & JetBrains Mono</span>
          </div>
        </footer>
      </main>
    </div>
  );
}

function firstName(me: Me): string {
  if (me.name) return me.name.split(' ')[0];
  return me.email.split('@')[0];
}

function weekOfYear(d: Date): number {
  const onejan = new Date(d.getFullYear(), 0, 1);
  return Math.ceil(((d.getTime() - onejan.getTime()) / 86400000 + onejan.getDay() + 1) / 7);
}

function DecorativeArc() {
  // A single hand-drawn curve sweeping behind the hero. The only ocean motif.
  return (
    <svg
      aria-hidden
      className="pointer-events-none absolute inset-x-0 -top-20 mx-auto h-[520px] w-[140%] -translate-x-[20%] opacity-[0.07] md:opacity-[0.09]"
      viewBox="0 0 1400 520"
      fill="none"
    >
      <path
        d="M-50 380 C 250 200, 500 540, 800 320 S 1300 100, 1500 280"
        stroke="#F4ECDD"
        strokeWidth="1.5"
      />
      <path
        d="M-50 420 C 250 240, 500 580, 800 360 S 1300 140, 1500 320"
        stroke="#FF5A3C"
        strokeWidth="1"
      />
      <path
        d="M-50 460 C 250 280, 500 620, 800 400 S 1300 180, 1500 360"
        stroke="#F4ECDD"
        strokeWidth="1"
        strokeDasharray="4 8"
      />
    </svg>
  );
}

function TilesSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="relative h-64 animate-pulse overflow-hidden rounded-sm border hair bg-ink-800"
        >
          <span className="absolute inset-y-0 left-0 w-1.5 bg-cream-200" />
        </div>
      ))}
    </div>
  );
}

function EmptyState({ isAdmin }: { isAdmin: boolean }) {
  return (
    <div className="relative overflow-hidden rounded-sm border-2 border-dashed border-cream-200 p-16 text-center">
      <p className="font-display text-3xl tracking-tightish text-cream">No systems yet.</p>
      <p className="mt-3 font-mono text-[11px] uppercase tracking-widestest text-cream-500">
        {isAdmin ? 'Open Admin to publish your first entry.' : 'Ask an editor to add some.'}
      </p>
    </div>
  );
}
